import { ClientRoute } from 'app/route/client';
import { Breadcrumbs } from 'component/Breadcrumbs/Breadcrumbs';
import {
  CompanyDto,
  CompanyModel,
  mapCompanyDtoToModel,
  mapCompanyModelToDto,
} from 'api/dto/Company.dto';
import { JobModel, mapJobDtoToModel } from 'api/dto/Job.dto';
import { LifecycleType } from 'model/Lifecycle';
import { mapPageDtoToModel, PageModel } from 'api/dto/Page.dto';
import { PageItemModel } from 'api/dto/PageItem.dto';
import { PageItemKind } from 'model/PageItemKind';
import { ProjectImage } from 'model/ProjectImage';
import { ProjectKind } from 'model/ProjectKind';
import {
  mapProjectLayoutDtoToModel,
  ProjectLayoutDto,
  ProjectLayoutModel,
} from 'api/dto/ProjectLayout.dto';
import {
  mapProjectSettingsDtoToModel,
  ProjectSettingsDto,
  ProjectSettingsModel,
} from 'api/dto/ProjectSettings.dto';
import { ProjectSortBy } from 'model/ProjectSortBy';
import { ProjectStatus } from 'model/ProjectStatus';
import { ProjectTemplateDto, ProjectTemplateModel } from 'api/dto/ProjectTemplate.dto';
import { QuestionOpinion } from 'model/QuestionOpinion';
import dayjs, { Dayjs } from 'dayjs';
import { compilePath } from 'router/compilePath';
import { DistributionApi } from 'api/DistributionApi';
import { PaginationDto } from 'model/Pagination.dto';

import { JobDto, mapJobExtendedModelToDto } from './JobExtended.dto';
import { DEFAULT_TIMEZONE } from './Timezone.dto';

export type ProjectPostDto = {
  companyId: number;
  kind: ProjectKind;
};

export type ProjectPaginatedDto = PaginationDto & {
  _embedded: {
    items: ProjectDto[];
  };
};

export type ProjectDto = ProjectTemplateDto & {
  company?: CompanyDto & { logoFile?: File };
  circleId?: string;
  companyId?: number;
  companyName?: string;
  createdInPast?: boolean;
  dirty: boolean;
  distributions?: DistributionApi.Entry[];
  isKioskDistributionEmpty?: boolean;
  isPublished: boolean;
  jobs?: JobDto[];
  layout?: ProjectLayoutDto;
  liveStart: string;
  liveStop: string;
  pulseFrequencyId?: string;
  reportRecipients?: string[];
  responseCompleted: number;
  responsePartial: number;
  responseRate: number;
  responseStarted: number;
  settings?: ProjectSettingsDto;
  status: number;
  lifecycleType?: LifecycleType;
};

export type ProjectModel = ProjectTemplateModel & {
  status: ProjectStatus;
  company?: CompanyModel;
  liveStart: Dayjs;
  liveStop: Dayjs;
  pulseFrequencyId?: string | null;
  responseRate: number;
  responsePartial?: number;
  responseStarted?: number;
  responseCompleted?: number;
  reportRecipients?: string[];
  jobs?: JobModel[];
  layout?: ProjectLayoutModel;
  settings?: ProjectSettingsModel;
  dirty?: boolean;
  isPublished?: boolean;
  isKioskDistributionEmpty?: boolean;
  createdInPast?: boolean;
  lifecycleType?: LifecycleType;
};

export function mapProjectModelToDto(model: ProjectModel): ProjectDto {
  return {
    id: model.id,
    name: model.name,
    company: model.company && mapCompanyModelToDto(model.company),
    status: model.status,
    liveStop: model.liveStop && model.liveStop.toISOString(),
    liveStart: model.liveStart && model.liveStart.toISOString(),
    createdAt: model.createdAt && model.createdAt.toISOString(),
    updatedAt: model.updatedAt && model.updatedAt.toISOString(),
    createdBy: model.createdBy,
    updatedBy: model.updatedBy,
    reportRecipients: model.reportRecipients && model.reportRecipients,
    jobs:
      model.jobs &&
      model.jobs.map((job) => (job.job ? mapJobExtendedModelToDto(job.job) : undefined)),
    responseRate: model.responseRate,
    responseStarted: model.responseStarted,
    responsePartial: model.responsePartial,
    responseCompleted: model.responseCompleted,
    categories: model.categories,
    dirty: model.dirty,
    isPublished: model.isPublished,
    kind: model.kind,
    isKioskDistributionEmpty: model.isKioskDistributionEmpty,
    createdInPast: model.createdInPast,
    lifecycleType: model.lifecycleType,
  };
}

export function mapProjectDtoToModel(dto: ProjectDto): ProjectModel {
  const formattedLiveStart = dto.liveStart && dto.liveStart.split('T')[0];
  const formattedLiveStop = dto.liveStop && dto.liveStop.split('T')[0];

  return {
    id: dto.id,
    name: dto.name,
    company: dto.company
      ? mapCompanyDtoToModel(dto.company)
      : dto.companyName && {
          name: dto.companyName,
          circleId: dto.circleId,
          id: dto.companyId,
          timezone: DEFAULT_TIMEZONE,
          settings: {
            ssoEnabled: false,
            multiLanguageEnabled: false,
            enabledLocales: [],
            enabledDashboardLocales: [],
            customKioskCodesEnabled: false,
            aiSummariesEnabled: false,
          },
        },
    status: dto.status,
    liveStart: dto.liveStart && dayjs(formattedLiveStart),
    liveStop: dto.liveStop && dayjs(formattedLiveStop),
    pulseFrequencyId: dto.pulseFrequencyId || null,
    createdAt: dto.createdAt && dayjs.utc(dto.createdAt),
    updatedAt: dto.updatedAt && dayjs.utc(dto.updatedAt),
    createdBy: dto.createdBy,
    updatedBy: dto.updatedBy,
    reportRecipients: dto.reportRecipients,
    responseRate: dto.responseRate,
    responsePartial: dto.responsePartial || 0,
    responseStarted: dto.responseStarted || 0,
    responseCompleted: dto.responseCompleted || 0,
    pages: (dto.pages || []).map((pagedto) => mapPageDtoToModel(pagedto)),
    layout: dto.layout && mapProjectLayoutDtoToModel(dto.layout),
    settings: dto.settings && mapProjectSettingsDtoToModel(dto.settings),
    jobs: dto.jobs && dto.jobs.map((job) => mapJobDtoToModel(job)),
    categories: dto.categories || [],
    dirty: dto.dirty,
    isPublished: dto.isPublished,
    kind: dto.kind,
    isKioskDistributionEmpty: dto.isKioskDistributionEmpty,
    createdInPast: dto.createdInPast,
    lifecycleType: dto.lifecycleType,
  };
}

export namespace ProjectModel {
  export const STATUSES: ProjectStatus[] = [
    ProjectStatus.InDevelopment,
    ProjectStatus.Live,
    ProjectStatus.Closed,
    ProjectStatus.Deleted,
  ];

  export const SURVEY_TYPE: ProjectKind[] = [
    ProjectKind.PROJECT_CLIENT_ADVOCACY_SCORE,
    ProjectKind.PROJECT_ENGAGEMENT_SCORE,
    ProjectKind.PROJECT_OVERALL_SCORE,
  ];

  export const SORT_BYS: ProjectSortBy[] = [ProjectSortBy.LastModified, ProjectSortBy.LiveDates];

  export const IMAGE_ACCEPT: ProjectImage[] = [
    ProjectImage.PNG,
    ProjectImage.JPG,
    ProjectImage.JPEG,
  ];

  export function getAcceptedImagesTypes(): string {
    return IMAGE_ACCEPT.toString();
  }

  export function getFixableOnly(projects: ProjectModel[]): ProjectModel[] {
    return projects.filter((project) => isFixableOnly(project));
  }

  export function sortByLiveStop(a: ProjectModel, b: ProjectModel): number {
    const aHasStop = Boolean(a.liveStop?.isValid());
    const bHasStop = Boolean(b.liveStop?.isValid());
    if (aHasStop && bHasStop) {
      return b.liveStop.unix() - a.liveStop.unix();
    }
    if (aHasStop !== bHasStop) {
      return aHasStop ? -1 : 1;
    }
    return b.id - a.id;
  }

  export function isEngagementScoreRelated(kind: ProjectKind): boolean {
    switch (kind) {
      case ProjectKind.PROJECT_ENGAGEMENT_SCORE:
      case ProjectKind.PULSE_PROJECT:
      case ProjectKind.PULSE_TEMPLATE:
        return true;
      default:
        return false;
    }
  }

  export function isPulseTypeProject(kind: ProjectKind): boolean {
    switch (kind) {
      case ProjectKind.PULSE_PROJECT:
      case ProjectKind.PULSE_TEMPLATE:
      case ProjectKind.PULSE_ON_DEMAND_PROJECT:
      case ProjectKind.PULSE_LIFECYCLE_PROJECT:
        return true;
      default:
        return false;
    }
  }

  export function lastPublishedProjectId(projects: ProjectModel[]): number | undefined {
    if (!projects || !(projects.length > 0)) {
      return undefined;
    }

    const last = [...projects]
      .sort((a, b) => b.id - a.id)
      .find((project) => project && project.isPublished);
    return last && last.id;
  }

  export function isCAS(project: ProjectModel): boolean {
    return project.kind === ProjectKind.PROJECT_CLIENT_ADVOCACY_SCORE;
  }

  export function isEngagementScore(project: ProjectModel): boolean {
    return project.kind === ProjectKind.PROJECT_ENGAGEMENT_SCORE;
  }

  export function isInDevelopment(project: ProjectModel): boolean {
    return project.status === ProjectStatus.InDevelopment;
  }

  export function isLive(project: ProjectModel): boolean {
    return project.status === ProjectStatus.Live;
  }

  export function isClosed(project: ProjectModel): boolean {
    return project.status === ProjectStatus.Closed;
  }

  export function isDeleted(project: ProjectModel): boolean {
    return project.status === ProjectStatus.Deleted;
  }

  export function isEditable(project: ProjectModel): boolean {
    return isInDevelopment(project) || isLifecycleProject(project);
  }

  export function isFixableOnly(project: ProjectModel): boolean {
    return isLive(project) || isClosed(project);
  }

  export function isCloseable(project: ProjectModel): boolean {
    return isInDevelopment(project) || isLive(project);
  }

  export function isDeletable(project: ProjectModel): boolean {
    return isInDevelopment(project) || isClosed(project);
  }

  export function isDemandPoll(project: ProjectModel): boolean {
    return project.kind === ProjectKind.PULSE_ON_DEMAND_PROJECT;
  }

  export function isLifecycleProject(project: ProjectModel): boolean {
    return project.kind === ProjectKind.PULSE_LIFECYCLE_PROJECT;
  }

  export function getPageByPageItem(project: ProjectModel, item: PageItemModel): PageModel {
    return (project.pages || []).find((page) => page.items.includes(item));
  }

  export function getNextPagesByPageItem(project: ProjectModel, item: PageItemModel): PageModel[] {
    const currentPage = getPageByPageItem(project, item);
    return (project.pages || []).filter((page) => page.position > currentPage.position);
  }

  export function getQuestionLibrary(project: ProjectModel): QuestionOpinion[] | null {
    if (project.kind !== ProjectKind.PULSE_TEMPLATE) {
      return null;
    }

    if (!project.pages[0]) {
      return null;
    }

    const items = project.pages[0].items;
    return items.filter((item) => item.kind === PageItemKind.QuestionOpinion) as QuestionOpinion[];
  }

  export function getBreadcrumbsPath(
    project: ProjectModel,
    pulseTemplateId: ProjectModel['id'],
    isOnboardSurveysEnabled = false,
  ): Breadcrumbs.PathItem[] {
    // TODO: This code is up for refactor when adding new lifecycle types.
    const lifecycleType = project.lifecycleType;

    if (isOnboardSurveysEnabled && lifecycleType === LifecycleType.ONBOARD) {
      return [
        {
          label: 'Onboard Surveys',
          href: compilePath(ClientRoute.Project.LifecycleSurveys.ONBOARD, { id: pulseTemplateId }),
        },
        {
          label: project.name,
        },
      ];
    }

    if (lifecycleType) {
      const lifecycleUrl = compilePath(ClientRoute.Project.Survey.Develop.QUESTION_PATTERN, {
        id: pulseTemplateId,
        survey_id: project.id,
      });
      return [
        {
          label: 'Employee Lifecycle Surveys',
        },
        {
          label: lifecycleType,
          href: lifecycleUrl,
        },
      ];
    } else {
      const liveAndScheduled = [ProjectStatus.InDevelopment, ProjectStatus.Live];
      const labelPrefix = liveAndScheduled.includes(project.status)
        ? 'Live and Scheduled'
        : 'Archived';

      const labelSuffix = isDemandPoll(project) ? 'Polls' : 'Surveys';

      let url;

      if (isDemandPoll(project)) {
        url = liveAndScheduled.includes(project.status)
          ? ClientRoute.Project.DemandPoll.LIVE_AND_SCHEDULED
          : ClientRoute.Project.DemandPoll.ARCHIVED;
      } else {
        url = liveAndScheduled.includes(project.status)
          ? ClientRoute.Project.Survey.LIVE_AND_SCHEDULED
          : ClientRoute.Project.Survey.ARCHIVED;
      }

      return [
        {
          label: `${labelPrefix} ${labelSuffix}`,
          href: compilePath(url, { id: pulseTemplateId }),
        },
        {
          label: project.name,
          preserveCase: true,
          href: compilePath(ClientRoute.Project.Survey.Develop.QUESTION_PATTERN, {
            id: pulseTemplateId,
            survey_id: project.id,
          }),
        },
      ];
    }
  }
}
